import {
  DocumentNode,
  MaybeMasked,
  OperationVariables,
  Unmasked,
  useApolloClient,
  useQuery,
} from "@apollo/client";
import { Search } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  ListItemButton,
} from "@mui/material";
import { ReactElement, ReactNode, useCallback, useMemo, useState } from "react";

import {
  GetNgpVanIntegrationQuery,
  GetNgpVanIntegrationQueryVariables,
  NgpVanObjectRefreshJobFieldsFragment,
  NgpVanObjectType,
} from "~frontend/__generated__/graphql";
import { GET_NGPVAN_INTEGRATION } from "~frontend/graphql/integration";
import theme, { palette } from "~frontend/theme";
import useDebounce from "~frontend/util/useDebounce";

import ErrorCard from "../../ErrorCard";
import { FlexBox } from "../../FlexBox";
import Loading from "../../Loading";
import FormTextField from "../FormTextField";
import JobControls from "./JobControls";
import { BaseProps, Integration } from "./types";

const MAX_IN_MEMORY_SEARCH = 100;

export default function NgpVanObjectAutocomplete<
  ObjectType extends Record<string, unknown>,
  GetObjectQuery,
  GetObjectQueryVariables extends OperationVariables,
  ListObjectQuery,
  ListObjectQueryVariables extends OperationVariables,
>({
  label,
  error,
  helperText,
  integrationId,
  databaseMode,
  readOnly,
  noResultsLabel,
  errorLabel,
  value,
  onChange,

  objectType,
  autoRefreshWindowMillis,

  getLastRefreshTime,
  getLastRefreshJob,

  getObjectQuery,
  getObjectQueryVariables,
  getObjectQueryResultToObject,
  objectToGetObjectQueryResult,

  listObjectQuery,
  listObjectQueryVariables,
  listObjectQueryResultToObjects,

  searchTextFromObject,
  valueFromObject,
  listItemFromObject,
}: BaseProps & {
  onChange: (newObject: ObjectType | null) => void;
  objectType: NgpVanObjectType;
  autoRefreshWindowMillis?: number;
  noResultsLabel: string;
  errorLabel: string;

  getLastRefreshTime: (integration: Integration) => Date | null;
  getLastRefreshJob: (
    integration: Integration
  ) => NgpVanObjectRefreshJobFieldsFragment | null;

  getObjectQuery: DocumentNode;
  getObjectQueryVariables: (value: number) => GetObjectQueryVariables;
  getObjectQueryResultToObject: (
    data: MaybeMasked<GetObjectQuery>
  ) => ObjectType | null;
  objectToGetObjectQueryResult: (
    object: ObjectType
  ) => Unmasked<GetObjectQuery>;

  listObjectQuery: DocumentNode;
  listObjectQueryVariables: (
    nameContains: string | null
  ) => ListObjectQueryVariables;
  listObjectQueryResultToObjects: (
    object: MaybeMasked<ListObjectQuery>
  ) => ObjectType[];

  searchTextFromObject: (object: ObjectType) => string;
  valueFromObject: (object: ObjectType) => number;
  listItemFromObject: (object: ObjectType) => ReactNode;
}): ReactElement {
  const client = useApolloClient();

  // The selected object query is used to fetch the object that is currently
  // selected (for when the autocomplete has a default value)
  const selectedObjectQuery = useQuery<GetObjectQuery, GetObjectQueryVariables>(
    getObjectQuery,
    {
      variables: value ? getObjectQueryVariables(value) : undefined,
      skip: !value,
    }
  );

  // the integration query loads metadata about the integration, including
  // the status of all refresh jobs
  const integrationQuery = useQuery<
    GetNgpVanIntegrationQuery,
    GetNgpVanIntegrationQueryVariables
  >(GET_NGPVAN_INTEGRATION, {
    variables: { input: { integrationId } },
    skip: readOnly,
  });

  // Next, we try to load all of the objects from the backend. The backend
  // will only return up to 101 -- if we get back >100, that means there are
  // too many objects to load into memory so we'll have to do a backend-based
  // search
  const allObjectsQuery = useQuery<ListObjectQuery, ListObjectQueryVariables>(
    listObjectQuery,
    {
      variables: listObjectQueryVariables(null),
      skip: readOnly,
      fetchPolicy: "network-only",
    }
  );

  const allObjects = useMemo(
    () =>
      allObjectsQuery.data
        ? listObjectQueryResultToObjects(allObjectsQuery.data)
        : [],
    [listObjectQueryResultToObjects, allObjectsQuery.data]
  );

  // Determine if we're able to load all object into memory and do an
  // in-memory search
  const canUseInMemorySearch = allObjects.length <= MAX_IN_MEMORY_SEARCH;

  const [search, setSearch] = useState<string>("");

  // If we can't search in memory, we have a separate query to load up
  // the search results from the backend once the user has entered at least
  // 3 characters. We debouce to avoid re-querying on every keystroke
  const debouncedSearch = useDebounce(search, 500);
  const searchObjectQuery = useQuery<ListObjectQuery, ListObjectQueryVariables>(
    listObjectQuery,
    {
      variables: listObjectQueryVariables(debouncedSearch),
      skip: search.length < 3 || canUseInMemorySearch,
      fetchPolicy: "network-only",
    }
  );
  const searchObjects = useMemo(
    () =>
      searchObjectQuery.data
        ? listObjectQueryResultToObjects(searchObjectQuery.data)
        : [],
    [listObjectQueryResultToObjects, searchObjectQuery.data]
  );

  // Helper to refetch integration metadata after a refresh job
  const refetchIntegrationData = useCallback(() => {
    integrationQuery.refetch().catch(console.error);
    allObjectsQuery.refetch().catch(console.error);
  }, [integrationQuery, allObjectsQuery]);

  // If the selected object query or integration query is errored/loading,
  // we can't yet show the autocomplete
  if (selectedObjectQuery.error) {
    return <ErrorCard errors={[selectedObjectQuery.error]} />;
  }

  if (integrationQuery.error) {
    return <ErrorCard errors={[integrationQuery.error]} />;
  }

  if (selectedObjectQuery.loading || integrationQuery.loading) {
    return <Loading />;
  }

  // Construct the list of options: for an in-memory search, this is either
  // all options (if the user hasn't entered a search term) or a client-side
  // filtered list. For a backend search, this is the list of search results
  // from the backend
  let options: (ObjectType | { _message: string })[] = [];
  let noOptionsText = noResultsLabel;
  if (allObjectsQuery.error || searchObjectQuery.error) {
    noOptionsText = errorLabel;
  } else if (canUseInMemorySearch) {
    // frontend-driven search
    if (search) {
      options = allObjects.filter(object =>
        searchTextFromObject(object)
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    } else {
      options = allObjects;
    }
  } else {
    // backend-driven search
    if (search.length < 3) {
      noOptionsText = "Enter at least 3 characters to search...";
    } else if (searchObjects.length <= MAX_IN_MEMORY_SEARCH) {
      options = searchObjects;
    } else {
      options = [
        ...searchObjects.slice(0, MAX_IN_MEMORY_SEARCH),
        { _message: "More than 100 results, please refine your search..." },
      ];
    }
  }

  // We are in a loading state if the all-objects query is loading, if we're
  // doing a backend search and the search results are loading, or if we're
  // doing a backend search and the search term has changed and we haven't
  // yet issued the updated search query (debouncedSearch !== search)
  let loading = false;
  if (allObjectsQuery.loading) {
    loading = true;
  } else if (!canUseInMemorySearch) {
    loading =
      search.length >= 3 &&
      (searchObjectQuery.loading || debouncedSearch !== search);
  }

  return (
    <FlexBox flexDirection="row">
      <Autocomplete<ObjectType | { _message: string }>
        sx={{ flex: 1 }}
        value={
          value ? getObjectQueryResultToObject(selectedObjectQuery.data!) : null
        }
        onChange={(_, newValue) => {
          if (newValue && "_message" in newValue) {
            return;
          }

          if (newValue) {
            // We write the value from the list to the apollo cache so that
            // we don't have to refetch the object (selectedObjectQuery will just
            // grab this cached value)
            client.writeQuery({
              query: getObjectQuery,
              variables: getObjectQueryVariables(valueFromObject(newValue)),
              data: objectToGetObjectQueryResult(newValue),
            });
          }

          onChange(newValue);
        }}
        inputValue={search}
        onInputChange={(_, newSearch) => setSearch(newSearch)}
        options={options}
        noOptionsText={noOptionsText}
        isOptionEqualToValue={(option, value) => {
          if ("_message" in value || "_message" in option) {
            return false;
          }

          return valueFromObject(option) === valueFromObject(value);
        }}
        getOptionDisabled={option => "_message" in option}
        renderOption={(props, option) => {
          if ("_message" in option) {
            return (
              <ListItemButton
                key={option._message as string}
                disabled
                {...(props as unknown as React.HTMLAttributes<HTMLDivElement>)}
              >
                {option._message as string}
              </ListItemButton>
            );
          }

          return (
            <ListItemButton
              key={valueFromObject(option)}
              {...(props as unknown as React.HTMLAttributes<HTMLDivElement>)}
            >
              {listItemFromObject(option)}
            </ListItemButton>
          );
        }}
        getOptionLabel={option =>
          "_message" in option
            ? (option._message as string)
            : searchTextFromObject(option)
        }
        getOptionKey={option =>
          "_message" in option
            ? (option._message as string)
            : valueFromObject(option)
        }
        filterOptions={options => options}
        loading={
          allObjectsQuery.loading ||
          (!canUseInMemorySearch &&
            search.length >= 3 &&
            (searchObjectQuery.loading || debouncedSearch !== search))
        }
        loadingText="Loading..."
        disabled={readOnly}
        renderInput={params => (
          <FormTextField
            {...params}
            label={label}
            error={error}
            helperText={helperText}
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment
                    position="start"
                    sx={{ padding: theme.spacing(1) }}
                  >
                    <Search />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                      sx={{ marginRight: "64px" }}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              sx: {
                border: `1px solid ${palette.midGray} !important`,
                fontSize: "14px",
                padding: "2.5px !important",
              },
              inputProps: {
                ...params.inputProps,
                sx: {
                  padding: "7.5px 4px 7.5px 5px !important",
                },
              },
            }}
          />
        )}
      />
      {readOnly ? null : (
        <Box
          sx={{
            width: "48px",
            paddingTop: "24px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-end",
          }}
        >
          <JobControls
            lastRefreshTime={getLastRefreshTime(
              integrationQuery.data!.getNgpVanIntegration
            )}
            lastRefreshJob={getLastRefreshJob(
              integrationQuery.data!.getNgpVanIntegration
            )}
            integrationId={integrationId}
            databaseMode={databaseMode}
            objectType={objectType}
            refetchIntegration={refetchIntegrationData}
            autoRefreshWindowMillis={autoRefreshWindowMillis}
          />
        </Box>
      )}
    </FlexBox>
  );
}
